import { buildKeypath } from '@/data/idGenerator';
import type { FieldDefinition, Report } from '@/data/schemas';
import { VariableResolver } from '@/data/variableResolver';
import type { EditingState } from './editingState';
import { formatValue, getDefaultValue } from './valueFormatters';

interface CreateFieldDisplayItemOptions {
  field: FieldDefinition;
  sectionId: string;
  report: Report;
  resolver: VariableResolver | null;
  locale: string;
  highlights: Record<string, unknown>;
  indices?: number[];
  editingState: EditingState;
  editingReason?: string;
  sectionDisableHighlight?: boolean;
}

export function createFieldDisplayItem({
  field,
  sectionId,
  report,
  resolver,
  locale,
  highlights,
  indices,
  editingState,
  editingReason,
  sectionDisableHighlight = false,
}: CreateFieldDisplayItemOptions) {
  const keypath = buildKeypath(sectionId, field.id, indices);
  const rawValue = getFieldValue(field, keypath, report, resolver);
  const fieldLocale = field.formatting?.locale ?? locale;
  const highlightEnabled = !(field.disableHighlight || sectionDisableHighlight);

  return {
    label: field.label,
    value: formatValue(rawValue, field, fieldLocale),
    rawValue,
    highlight: highlightEnabled && highlights[keypath] !== undefined,
    fieldId: field.id,
    keypath,
    fieldType: field.type,
    fieldFormat: field.format,
    fieldOptions: field.options,
    fieldSource: field.source,
    isComputed: editingState === 'computed' || (editingState !== 'editable' && !!field.source),
    editingState,
    editingReason,
  };
}

function getFieldValue(
  field: FieldDefinition,
  keypath: string,
  report: Report,
  resolver: VariableResolver | null
): unknown {
  if (keypath in report.data.values) {
    return report.data.values[keypath];
  }

  if (resolver && field.source) {
    try {
      return resolver.resolveSource(field.source, field.type);
    } catch {
      // Ignore resolution errors and fall through to default value
    }
  }

  return getDefaultValue(field.type);
}
